/** 4.7 Slab and 4.8 Rows: one surface slab with carved cuts between rows and an ochre notch for navigation. */
import type { MouseEvent, ReactElement, ReactNode } from "react";
import { Link } from "react-router-dom";
import { Icon } from "./Icon";

export function Slab({ children, className = "", radius = 20, as: Tag = "div", ...rest }: { children: ReactNode; className?: string; radius?: 20 | 24; as?: "div" | "ul" | "section"; id?: string; "aria-label"?: string }): ReactElement {
  return <Tag className={`slab ${radius === 24 ? "slab--r24" : ""} ${className}`} {...rest}>{children}</Tag>;
}

export type RowKind = "nav" | "dim" | "plain";
export interface RowProps {
  title: ReactNode;
  sub?: ReactNode;
  /** Trailing value (small-b accent by default). */
  trailing?: ReactNode;
  trailingTone?: "accent" | "text";
  kind?: RowKind;
  height?: 58 | 56 | 52 | 50 | 48 | 60;
  lead?: ReactNode;
  to?: string;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  /** Let the sub wrap to two lines (default: one line with an ellipsis). */
  wrapSub?: boolean;
  className?: string;
  "aria-label"?: string;
}
export function Row({ title, sub, trailing, trailingTone = "accent", kind = "plain", height = 58, lead, to, onClick, wrapSub = false, className = "", ...rest }: RowProps): ReactElement {
  const nav = kind === "nav" || kind === "dim";
  const cls = `rowi rowi--${height} ${nav ? "rowi--nav" : ""} ${kind === "dim" ? "rowi--dim" : ""} ${className}`;
  const body = <>
    {lead && <span className="rowi__lead">{lead}</span>}
    <span className="rowi__main">
      <span className="rowi__title">{title}</span>
      {sub !== undefined && sub !== null && <span className={`rowi__sub ${wrapSub ? "rowi__sub--wrap" : ""}`}>{sub}</span>}
    </span>
    {trailing !== undefined && trailing !== null && <span className={`rowi__trailing ${trailingTone === "text" ? "rowi__trailing--text" : ""}`}>{trailing}</span>}
    {nav && <span className="rowi__notch" aria-hidden="true"><Icon name="chevron-right" size={20} stroke={2.4} /></span>}
  </>;
  if (to) return <Link to={to} className={cls} onClick={onClick as (e: MouseEvent<HTMLAnchorElement>) => void} aria-label={rest["aria-label"]}>{body}</Link>;
  if (onClick) return <button type="button" className={cls} onClick={onClick} aria-label={rest["aria-label"]}>{body}</button>;
  return <div className={cls} aria-label={rest["aria-label"]}>{body}</div>;
}

export function Rows({ children, className = "", ...rest }: { children: ReactNode; className?: string; "aria-label"?: string; id?: string }): ReactElement {
  return <Slab className={`rows ${className}`} {...rest}>{children}</Slab>;
}
